import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Warning, Plus, Minus, ShoppingCart, CheckCircle, Users, Clock, X, Sparkle, ForkKnife, ShieldCheck, Buildings } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { suggestQuantities } from '@/data/cateringMenu';
import { ProgressStepper } from '@/components/ProgressStepper';
import { TagBadge } from '@/components/TagBadge';
import { CateringConfirmation } from '@/components/catering/CateringConfirmation';
import { CateringReviewModal } from '@/components/catering/CateringReviewModal';
import { CateringServiceCards } from '@/components/catering/CateringServiceCards';
import { EmployeeMealEnquiryForm } from '@/components/catering/EmployeeMealEnquiryForm';
import { FestivalEnquiryForm } from '@/components/catering/FestivalEnquiryForm';

const NOVA_LOGO = "https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MIN_MEALS = 20;
const MAX_MEALS = 500;

const COPY = {
  corporate: {
    badge: 'Corporate & Events',
    heading1: 'Catering that',
    headingHighlight: 'delights',
    heading2: 'every occasion.',
    subheading: 'From corporate events and daily employee meals to festive celebrations — freshly prepared food, delivered with care.',
    formTitle: 'Your Event Details',
    companyLabel: 'Company Name *',
    companyPlaceholder: '',
    contactLabel: 'Contact Person *',
    eventNameLabel: 'Event Name *',
    eventNamePlaceholder: 'e.g., Annual Team Meeting',
    submitButtonPrefix: 'Review Order',
    orderType: 'corporate',
    peopleHelperTitle: 'How many people are you serving?',
  },
  bulk: {
    badge: 'House Parties & Events',
    heading1: 'Bulk orders for',
    headingHighlight: 'gatherings',
    heading2: 'that matter.',
    subheading: 'Chef-crafted menus for house parties, birthdays, festivals, and family get-togethers — delivered fresh, ready to serve.',
    formTitle: 'Your Party Details',
    companyLabel: 'Host / Family Name *',
    companyPlaceholder: 'e.g., The Sharma Family',
    contactLabel: 'Contact Person *',
    eventNameLabel: 'Event / Occasion *',
    eventNamePlaceholder: 'e.g., Anniversary Party, Housewarming',
    submitButtonPrefix: 'Review Order',
    orderType: 'bulk',
    peopleHelperTitle: 'How many guests are you hosting?',
  },
};

const CorporateCateringPage = ({ mode = 'corporate' }) => {
  const navigate = useNavigate();
  const copy = COPY[mode] || COPY.corporate;
  const [cateringMenu, setCateringMenu] = useState([]);
  const [cart, setCart] = useState({});
  const [activeCategory, setActiveCategory] = useState('');
  const [peopleCount, setPeopleCount] = useState('');
  const [showCartMobile, setShowCartMobile] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    mobile: '',
    email: '',
    event_name: '',
    event_date: '',
    event_time: '',
    delivery_address: '',
    city: '',
    state: '',
    zipcode: '',
    special_instructions: '',
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load menu from API (live, admin-editable)
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/menu/catering`)
      .then(({ data }) => {
        setCateringMenu(data);
        if (data.length > 0 && !activeCategory) setActiveCategory(data[0].category);
      })
      .catch(() => {
        // fallback: nothing — menu just stays empty
      });
  }, []);

  // Flat list of all items (for cart lookup)
  const ALL_ITEMS = useMemo(() => cateringMenu.flatMap(c => c.items), [cateringMenu]);

  // Minimum event date (3 days ahead)
  const minEventDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().split('T')[0];
  }, []);

  const updateQty = (itemId, delta) => {
    setCart((prev) => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + delta);
      const updated = { ...prev };
      if (next === 0) delete updated[itemId];
      else updated[itemId] = next;
      return updated;
    });
  };

  const setQty = (itemId, value) => {
    const num = parseInt(value) || 0;
    setCart((prev) => {
      const updated = { ...prev };
      if (num <= 0) delete updated[itemId];
      else updated[itemId] = Math.min(num, MAX_MEALS);
      return updated;
    });
  };

  const applyPeopleSuggestion = () => {
    const count = parseInt(peopleCount);
    if (!count || count < 20) {
      toast.error('Please enter at least 20 people');
      return;
    }
    const suggestions = suggestQuantities(count);
    setCart(suggestions);
    toast.success(`Suggested quantities added for ${count} people. Edit as needed.`);
  };

  const cartDetails = useMemo(() => {
    const lines = Object.entries(cart).map(([id, qty]) => {
      const item = ALL_ITEMS.find((i) => i.id === id);
      return item ? { ...item, quantity: qty, subtotal: item.price * qty } : null;
    }).filter(Boolean);
    const totalMeals = lines.reduce((sum, l) => sum + l.quantity, 0);
    const subtotal = lines.reduce((sum, l) => sum + l.subtotal, 0);
    const gst = Math.round(subtotal * 0.05);
    const total = subtotal + gst;
    return { lines, totalMeals, subtotal, gst, total };
  }, [cart]);

  const isEventDateValid = () => {
    if (!formData.event_date) return true;
    return formData.event_date >= minEventDate;
  };

  const validateBeforeSubmit = () => {
    if (cartDetails.totalMeals < MIN_MEALS) {
      toast.error(`Minimum catering order is ${MIN_MEALS} meals. You have ${cartDetails.totalMeals}.`);
      return false;
    }
    if (cartDetails.totalMeals > MAX_MEALS) {
      toast.error(`For orders above ${MAX_MEALS} meals, please contact Nova Foods directly.`);
      return false;
    }
    const required = ['company_name', 'contact_person', 'mobile', 'email', 'event_name', 'event_date', 'event_time', 'delivery_address', 'city', 'state', 'zipcode'];
    for (const field of required) {
      if (!formData[field]?.trim()) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    if (!isEventDateValid()) {
      toast.error('Corporate catering requires a minimum 3-day advance booking. Please select another date.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!/^[0-9+\-\s]{10,15}$/.test(formData.mobile)) {
      toast.error('Please enter a valid mobile number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;
    // Show review modal before hitting API
    setShowReview(true);
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        items: cartDetails.lines.map((l) => ({ id: l.id, name: l.name, price: l.price, quantity: l.quantity })),
        people_count: parseInt(peopleCount) || null,
        order_type: copy.orderType,
        ...formData,
      };
      const resp = await axios.post(`${BACKEND_URL}/api/corporate-catering/submit`, payload);
      setShowReview(false);
      setConfirmation(resp.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToCategory = (cat) => {
    setActiveCategory(cat);
    const el = document.getElementById(`cat-${cat.replace(/\s+/g, '-')}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Premium input class (reused)
  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-5 py-3.5 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-2";

  // Derive checkout step (Cart → Details → Review → Success)
  const checkoutStep = useMemo(() => {
    if (confirmation) return 4;
    if (showReview) return 3;
    // step 2 (Details) once cart has items OR any form field is filled
    if (cartDetails.lines.length > 0 || Object.values(formData).some((v) => (v || '').toString().trim())) return 2;
    return 1;
  }, [confirmation, showReview, cartDetails.lines.length, formData]);

  // ============ CONFIRMATION SCREEN ============
  if (confirmation) {
    return (
      <CateringConfirmation
        confirmation={confirmation}
        email={formData.email}
        onGoHome={() => navigate('/')}
        onLogin={() => navigate('/login')}
      />
    );
  }

  // ============ MAIN PAGE ============
  return (
    <div className="min-h-screen bg-[#FAFAF7] overflow-x-hidden">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-2xl border-b border-stone-100 shadow-sm' : 'bg-transparent'}`}
        data-testid="catering-header"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 font-heading text-2xl font-semibold text-[#1A4331]">
            <img src={NOVA_LOGO} alt="Nova Foods" className="h-10 w-10 rounded-lg object-cover" />
            Nova Foods
          </button>
          <button
            onClick={() => navigate('/')}
            className="hidden md:flex items-center gap-1.5 text-stone-600 hover:text-[#1A4331] font-medium text-sm transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} weight="bold" /> Home
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative px-6 pt-10 pb-16 md:pt-16 md:pb-20 overflow-hidden" data-testid="catering-hero">
        {/* decorative orbs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="absolute top-0 -left-20 w-[420px] h-[420px] bg-[#34D399]/25 rounded-full blur-3xl"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="absolute -bottom-32 right-0 w-[500px] h-[500px] bg-[#1A4331]/15 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl border border-stone-200/60 text-[#1A4331] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-6 shadow-sm"
          >
            <Buildings size={14} weight="fill" /> {copy.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1C1917] font-heading mb-5"
            data-testid="catering-heading"
          >
            {copy.heading1} <span className="italic text-[#1A4331]">{copy.headingHighlight}</span><br className="hidden sm:block" /> {copy.heading2}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-base sm:text-lg leading-relaxed text-stone-600 max-w-2xl mx-auto mb-10"
          >
            {copy.subheading}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="flex flex-wrap items-center justify-center gap-3"
          >
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl border border-stone-200/60 text-stone-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
              <ShieldCheck size={16} weight="duotone" className="text-[#1A4331]" /> FSSAI Certified Kitchens
            </div>
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl border border-stone-200/60 text-stone-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
              <Clock size={16} weight="duotone" className="text-[#1A4331]" /> 3-day advance booking
            </div>
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl border border-stone-200/60 text-stone-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
              <ForkKnife size={16} weight="duotone" className="text-[#1A4331]" /> {MIN_MEALS}–{MAX_MEALS} meals
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICE SELECTION CARDS (corporate mode only) */}
      {mode === 'corporate' && <CateringServiceCards />}

      {/* PROGRESS STEPPER */}
      <section className="max-w-3xl mx-auto px-6 pb-4 relative z-20" data-testid="checkout-stepper-wrap" id="catering-menu-section">
        <div className="bg-white/70 backdrop-blur-xl border border-stone-200/60 rounded-2xl p-4 shadow-sm">
          <ProgressStepper steps={['Cart', 'Details', 'Review', 'Success']} current={checkoutStep} />
        </div>
      </section>

      {/* PEOPLE COUNT HELPER */}
      <section className="max-w-6xl mx-auto px-6 -mt-4 relative z-10" data-testid="people-suggestion">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative bg-gradient-to-br from-[#1A4331] via-[#204d38] to-[#2A5A44] text-white rounded-3xl p-6 md:p-8 shadow-2xl shadow-[#1A4331]/20 overflow-hidden"
        >
          {/* pattern */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '18px 18px' }} />
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#34D399]/20 rounded-full blur-3xl" />

          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Users size={26} weight="duotone" className="text-[#34D399]" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-medium font-heading tracking-tight">{copy.peopleHelperTitle}</h3>
                <p className="text-sm text-white/70 mt-1">We&apos;ll suggest a starter combination — you can fully customize below.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <div className="flex gap-2 flex-wrap">
                {[25, 50, 100, 250].map((n) => (
                  <button
                    key={n}
                    onClick={() => setPeopleCount(String(n))}
                    className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${peopleCount === String(n) ? 'bg-[#34D399] text-[#1A4331] shadow-lg shadow-[#34D399]/30' : 'bg-white/10 hover:bg-white/20 border border-white/15'}`}
                    data-testid={`people-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="20"
                  max="500"
                  value={peopleCount}
                  onChange={(e) => setPeopleCount(e.target.value)}
                  placeholder="Custom"
                  className="w-24 bg-white/10 border border-white/15 rounded-full px-4 py-2.5 text-sm outline-none placeholder:text-white/50 focus:bg-white/15 focus:border-[#34D399]/50 transition-all"
                  data-testid="people-input"
                />
                <button
                  onClick={applyPeopleSuggestion}
                  className="bg-[#34D399] text-[#1A4331] px-5 py-2.5 rounded-full text-sm font-bold hover:bg-white transition-all shadow-lg shadow-[#34D399]/20"
                  data-testid="suggest-btn"
                >
                  Suggest
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Advance booking notice */}
        <div className="mt-4 inline-flex items-start gap-2.5 bg-[#FEF3C7] border border-[#FCD34D]/60 text-[#92400E] px-4 py-2.5 rounded-2xl text-xs sm:text-sm">
          <Warning size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <p><span className="font-semibold">3-day advance booking required.</span> Orders above {MAX_MEALS} meals — please contact Nova Foods directly.</p>
        </div>
      </section>

      {/* MENU + SUMMARY GRID */}
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* MENU */}
        <div>
          {/* Sticky category pills */}
          <div className="sticky top-[76px] z-30 -mx-6 px-6 py-3 bg-[#FAFAF7]/90 backdrop-blur-2xl border-b border-stone-100 mb-8 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 min-w-max">
              {cateringMenu.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => scrollToCategory(cat.category)}
                  className={`px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all border ${activeCategory === cat.category
                    ? 'bg-[#1A4331] text-white border-[#1A4331] shadow-lg shadow-[#1A4331]/25'
                    : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]/40 hover:text-[#1A4331]'}`}
                  data-testid={`cat-nav-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="mr-1.5">{cat.icon}</span>{cat.category}
                </button>
              ))}
            </div>
          </div>

          {cateringMenu.map((cat, catIdx) => (
            <motion.div
              key={cat.category}
              id={`cat-${cat.category.replace(/\s+/g, '-')}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="mb-14"
              data-testid={`category-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-end justify-between mb-6">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">Category {String(catIdx + 1).padStart(2, '0')}</p>
                  <h2 className="text-2xl sm:text-3xl font-medium font-heading text-[#1C1917] tracking-tight flex items-center gap-2">
                    <span>{cat.icon}</span> {cat.category}
                  </h2>
                </div>
                <p className="text-xs text-stone-500 hidden sm:block">{cat.items.length} items</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {cat.items.map((item) => {
                  const qty = cart[item.id] || 0;
                  return (
                    <motion.div
                      key={item.id}
                      whileHover={{ y: -6 }}
                      transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                      className={`group bg-white border rounded-3xl overflow-hidden transition-all duration-300 ${qty > 0 ? 'border-[#1A4331] shadow-xl shadow-[#1A4331]/10 ring-2 ring-[#1A4331]/10' : 'border-stone-100 hover:shadow-2xl hover:shadow-stone-300/40 hover:border-stone-200'}`}
                      data-testid={`item-${item.id}`}
                    >
                      <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        {/* veg indicator */}
                        <div className="absolute top-3 left-3">
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center bg-white/95 backdrop-blur-sm ${item.isVeg ? 'border-green-600' : 'border-red-600'}`}>
                            <div className={`w-2.5 h-2.5 rounded-full ${item.isVeg ? 'bg-green-600' : 'bg-red-600'}`} />
                          </div>
                        </div>
                        {qty > 0 && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-3 right-3 bg-[#1A4331] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg"
                          >
                            {qty} in cart
                          </motion.div>
                        )}
                        {item.tag && qty === 0 && (
                          <div className="absolute top-3 right-3">
                            <TagBadge tag={item.tag} />
                          </div>
                        )}
                      </div>

                      <div className="p-5">
                        {item.tag && qty > 0 && (
                          <div className="mb-2">
                            <TagBadge tag={item.tag} />
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-[15px] text-[#1C1917] leading-tight tracking-tight">{item.name}</h3>
                        </div>
                        {item.description && <p className="text-xs text-stone-500 mb-3 leading-relaxed">{item.description}</p>}

                        <div className="flex items-center justify-between mt-4">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Per plate</p>
                            <p className="text-xl font-bold text-[#1A4331] font-heading tracking-tight">₹{item.price}</p>
                          </div>

                          {qty === 0 ? (
                            <button
                              onClick={() => updateQty(item.id, 1)}
                              className="bg-[#1A4331] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2A5A44] flex items-center gap-1.5 transition-all shadow-md shadow-[#1A4331]/20 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                              data-testid={`add-${item.id}`}
                            >
                              <Plus size={14} weight="bold" /> Add
                            </button>
                          ) : (
                            <motion.div
                              initial={{ scale: 0.9, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="flex items-center gap-1 bg-[#1A4331] text-white rounded-full p-1 shadow-md shadow-[#1A4331]/20"
                            >
                              <button
                                onClick={() => updateQty(item.id, -1)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-white/15 rounded-full transition-colors"
                                data-testid={`decrease-${item.id}`}
                              >
                                <Minus size={14} weight="bold" />
                              </button>
                              <input
                                type="number"
                                value={qty}
                                onChange={(e) => setQty(item.id, e.target.value)}
                                className="w-10 bg-transparent text-center text-sm font-bold outline-none"
                                data-testid={`qty-${item.id}`}
                              />
                              <button
                                onClick={() => updateQty(item.id, 1)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-white/15 rounded-full transition-colors"
                                data-testid={`increase-${item.id}`}
                              >
                                <Plus size={14} weight="bold" />
                              </button>
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}

          {/* CUSTOMER DETAILS FORM */}
          <motion.div
            id="customer-details"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-stone-100 rounded-3xl p-6 md:p-10 shadow-xl shadow-stone-200/40 mt-8"
            data-testid="catering-form"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-2xl bg-[#1A4331]/10 flex items-center justify-center">
                <span className="text-[#1A4331] font-bold text-lg font-heading">2</span>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">Almost there</p>
                <h2 className="text-2xl sm:text-3xl font-medium font-heading text-[#1C1917] tracking-tight">{copy.formTitle}</h2>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{copy.companyLabel}</label>
                  <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder={copy.companyPlaceholder} className={inputCls} required data-testid="cat-company-input" />
                </div>
                <div>
                  <label className={labelCls}>{copy.contactLabel}</label>
                  <input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className={inputCls} required data-testid="cat-contact-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Mobile Number *</label>
                  <input type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} className={inputCls} required data-testid="cat-mobile-input" />
                </div>
                <div>
                  <label className={labelCls}>Email Address *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} required data-testid="cat-email-input" />
                </div>
              </div>

              <div>
                <label className={labelCls}>{copy.eventNameLabel}</label>
                <input type="text" value={formData.event_name} onChange={(e) => setFormData({ ...formData, event_name: e.target.value })} placeholder={copy.eventNamePlaceholder} className={inputCls} required data-testid="cat-event-name-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Event Date * <span className="text-stone-400 font-normal normal-case tracking-normal">(min 3 days ahead)</span></label>
                  <input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    min={minEventDate}
                    className={`${inputCls} ${!isEventDateValid() ? '!border-red-400 focus:!border-red-500 focus:!ring-red-500/10' : ''}`}
                    required
                    data-testid="cat-event-date-input"
                  />
                  {formData.event_date && !isEventDateValid() && (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><Warning size={12} weight="fill" /> Minimum 3-day advance booking required.</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Event Time *</label>
                  <input type="time" value={formData.event_time} onChange={(e) => setFormData({ ...formData, event_time: e.target.value })} className={inputCls} required data-testid="cat-event-time-input" />
                </div>
              </div>

              <div>
                <label className={labelCls}>Delivery Address *</label>
                <input type="text" value={formData.delivery_address} onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })} placeholder="Building, Floor, Street" className={inputCls} required data-testid="cat-address-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className={labelCls}>City *</label>
                  <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className={inputCls} required data-testid="cat-city-input" />
                </div>
                <div>
                  <label className={labelCls}>State *</label>
                  <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className={inputCls} required data-testid="cat-state-input" />
                </div>
                <div>
                  <label className={labelCls}>Zipcode *</label>
                  <input type="text" value={formData.zipcode} onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })} className={inputCls} required data-testid="cat-zipcode-input" />
                </div>
              </div>

              <div>
                <label className={labelCls}>Special Instructions <span className="text-stone-400 font-normal normal-case tracking-normal">(Optional)</span></label>
                <textarea value={formData.special_instructions} onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })} rows="3" placeholder="Dietary restrictions, packaging preferences, etc." className={`${inputCls} resize-none`} data-testid="cat-instructions-input" />
              </div>

              <button
                type="submit"
                disabled={submitting || cartDetails.totalMeals < MIN_MEALS}
                className="w-full bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white px-6 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-[#1A4331]/30 hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none flex items-center justify-center gap-2 text-[15px]"
                data-testid="cat-submit-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>Review Order · ₹{cartDetails.total.toLocaleString()}</>
                )}
              </button>
              <p className="text-center text-xs text-stone-500">You&apos;ll review the full order before final submission.</p>
            </form>
          </motion.div>
        </div>

        {/* STICKY ORDER SUMMARY */}
        <aside className="hidden lg:block">
          <div
            className="sticky top-[104px] bg-white/80 backdrop-blur-2xl border border-stone-100 rounded-3xl shadow-2xl shadow-stone-300/30 p-6 overflow-hidden"
            data-testid="order-summary"
          >
            {/* decorative gradient */}
            <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 bg-[#34D399]/20 rounded-full blur-2xl" />

            <div className="relative">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-10 h-10 rounded-2xl bg-[#1A4331] flex items-center justify-center">
                  <ShoppingCart size={20} weight="duotone" className="text-[#34D399]" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Live Cart</p>
                  <h3 className="text-lg font-medium font-heading text-[#1C1917] tracking-tight">Order Summary</h3>
                </div>
              </div>

              {cartDetails.lines.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-stone-100 flex items-center justify-center">
                    <ShoppingCart size={28} weight="duotone" className="text-stone-400" />
                  </div>
                  <p className="text-stone-700 text-sm font-medium">Your cart is empty</p>
                  <p className="text-stone-400 text-xs mt-1">Add items from the menu to begin</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1 mb-4">
                    <AnimatePresence>
                      {cartDetails.lines.map((line) => (
                        <motion.div
                          key={line.id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 8 }}
                          className="flex items-center gap-3 text-sm border-b border-stone-100/80 pb-2.5"
                        >
                          <div className="w-9 h-9 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                            <img src={line.image} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#1C1917] font-medium leading-tight truncate">{line.name}</p>
                            <p className="text-xs text-stone-500">{line.quantity} × ₹{line.price}</p>
                          </div>
                          <p className="font-bold text-[#1A4331] text-sm">₹{line.subtotal.toLocaleString()}</p>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  <div className="bg-stone-50 rounded-2xl p-4 mb-4 space-y-2 text-sm">
                    <div className="flex justify-between text-stone-600">
                      <span>Total Meals</span>
                      <span className="font-semibold text-[#1C1917]">{cartDetails.totalMeals}</span>
                    </div>
                    <div className="flex justify-between text-stone-600">
                      <span>Subtotal</span>
                      <span className="font-semibold text-[#1C1917]">₹{cartDetails.subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-stone-600">
                      <span>GST (5%)</span>
                      <span className="font-semibold text-[#1C1917]">₹{cartDetails.gst.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-stone-200 pt-2.5 mt-1 flex justify-between items-end">
                      <span className="font-bold text-[#1C1917]">Grand Total</span>
                      <span className="text-xl font-bold text-[#1A4331] font-heading tracking-tight">₹{cartDetails.total.toLocaleString()}</span>
                    </div>
                  </div>

                  {cartDetails.totalMeals < MIN_MEALS && (
                    <div className="bg-amber-50 border border-amber-200/60 text-amber-800 text-xs p-3 rounded-2xl mb-3 flex items-start gap-2">
                      <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
                      <span>Add <b>{MIN_MEALS - cartDetails.totalMeals}</b> more meals to meet the minimum of {MIN_MEALS}.</span>
                    </div>
                  )}
                  {cartDetails.totalMeals > MAX_MEALS && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-2xl mb-3">
                      Above {MAX_MEALS} meals. Please contact Nova Foods directly.
                    </div>
                  )}

                  <button
                    onClick={() => document.getElementById('customer-details')?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full bg-[#1A4331] text-white px-4 py-3 rounded-full text-sm font-semibold hover:bg-[#2A5A44] transition-all shadow-lg shadow-[#1A4331]/20 hover:shadow-xl hover:-translate-y-0.5"
                    data-testid="proceed-to-form"
                  >
                    Proceed to Details →
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* ENQUIRY-ONLY SERVICES (corporate mode only) */}
      {mode === 'corporate' && (
        <>
          <EmployeeMealEnquiryForm />
          <FestivalEnquiryForm />
        </>
      )}

      {/* MOBILE STICKY CART BUTTON */}
      <AnimatePresence>
        {cartDetails.lines.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="lg:hidden fixed bottom-4 left-4 right-4 z-40"
          >
            <button
              onClick={() => setShowCartMobile(true)}
              className="w-full bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white px-5 py-3.5 rounded-full font-semibold shadow-2xl shadow-[#1A4331]/40 flex items-center justify-between backdrop-blur-xl"
              data-testid="mobile-cart-btn"
            >
              <span className="flex items-center gap-2.5">
                <div className="relative">
                  <ShoppingCart size={22} weight="duotone" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#34D399] text-[#1A4331] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {cartDetails.lines.length}
                  </span>
                </div>
                <span>{cartDetails.totalMeals} meals</span>
              </span>
              <span className="flex items-center gap-2">₹{cartDetails.total.toLocaleString()} <span className="text-[#34D399]">→</span></span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE CART DRAWER */}
      <AnimatePresence>
        {showCartMobile && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setShowCartMobile(false)}>
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 260 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-10 h-1 bg-stone-300 rounded-full mx-auto mb-5" />
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Live Cart</p>
                  <h3 className="text-xl font-medium font-heading tracking-tight">Order Summary</h3>
                </div>
                <button onClick={() => setShowCartMobile(false)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors">
                  <X size={20} weight="bold" />
                </button>
              </div>

              <div className="space-y-3 mb-5">
                {cartDetails.lines.map((line) => (
                  <div key={line.id} className="flex items-center gap-3 text-sm border-b border-stone-100 pb-3">
                    <div className="w-10 h-10 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                      <img src={line.image} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{line.name}</p>
                      <p className="text-xs text-stone-500">{line.quantity} × ₹{line.price}</p>
                    </div>
                    <span className="font-bold text-[#1A4331]">₹{line.subtotal.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="bg-stone-50 rounded-2xl p-4 mb-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span className="font-semibold">₹{cartDetails.subtotal.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-stone-600">GST (5%)</span><span className="font-semibold">₹{cartDetails.gst.toLocaleString()}</span></div>
                <div className="border-t border-stone-200 pt-2 flex justify-between items-end">
                  <span className="font-bold">Total</span>
                  <span className="text-xl font-bold text-[#1A4331] font-heading">₹{cartDetails.total.toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={() => { setShowCartMobile(false); document.getElementById('customer-details')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="w-full bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white py-3.5 rounded-full font-semibold shadow-lg shadow-[#1A4331]/20"
              >
                Proceed to Details →
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* REVIEW MODAL (Step 3 — Cart → Details → Review → Success) */}
      <CateringReviewModal
        open={showReview}
        onClose={() => setShowReview(false)}
        onConfirm={handleFinalSubmit}
        submitting={submitting}
        formData={formData}
        cartDetails={cartDetails}
        onEditDetails={() => { setShowReview(false); setTimeout(() => document.getElementById('customer-details')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
        onEditCart={() => { setShowReview(false); window.scrollTo({ top: 700, behavior: 'smooth' }); }}
      />

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
};

export default CorporateCateringPage;
